/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import SearchSelect from "@ui-components/Select/GenericSelect";
import { ReactComponent as GeolocationIcon } from "../../../../shared/assets/geolocation.svg";
import SegmentedControl from "@ui-components/SegmentedContol/GenericSegmentedContol";

import { GenericRadioCard } from "@ui-components/RadioCard/GenericRadioCard";
import { useDispatch, useSelector } from "react-redux";
import { fetchPricingPlans, Port, selectLocations, selectPricingPlans, setPricingPlans, updateOrder } from "@redux/slices/kontrolEdge/orderSlice";
import { dropdownItems } from "kontrolEdge/constants/constants";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import { useAppSelector } from "@redux/hooks";
import { calculatePricingOptions } from "./helpers/calcutalePricingOptions";
import { AppDispatch, RootState } from "@redux/store/store";

interface PortLocationForm {
  locationName: string;
  portCapacity: string;
  contractTerm: string;
  portType: "single" | "dual";
}

export interface PortLocationRef {
  submit: () => Promise<boolean>;
}

const PortLocation = forwardRef<PortLocationRef, { token: string | null }>(
  ({ token }, ref) => {
    // Redux selectors
    const selectedPortIndex =
      useSelector((state: RootState) => state.order.selectedPortIndex) ?? 0;
    const savedPort = useSelector(
      (state: RootState) => state.order.ports[selectedPortIndex]
    );
    const isEditing = useSelector((state: RootState) => state.order.isEditing);
    const dispatch = useDispatch<AppDispatch>();
    const locations = useSelector(selectLocations);
    const pricingPlans = useSelector(selectPricingPlans);

    console.log(savedPort, 'saved port');

    // Define default values.
    const defaultLocation = isEditing ? savedPort?.location?.datacenterId || "" : "";
    const defaultPortCapacity = isEditing
      ? savedPort?.configuration?.capacity?.speed?.toString() || "10000"
      : "10000";
    const defaultContractTerm = isEditing
      ? savedPort?.configuration?.term?.months
        ? savedPort.configuration.term.months.toString()
        : "12"
      : "12";
    const defaultPortType = isEditing
      ? (savedPort?.configuration?.capacity?.type as "single" | "dual") || "single"
      : "single";

    // Initialize form with default values.
    const {
      handleSubmit,
      control,
      reset,
      watch,
      formState: { errors },
    } = useForm<PortLocationForm>({
      mode: "onChange",
      defaultValues: {
        locationName: defaultLocation,
        portCapacity: "",
        contractTerm: "",
        portType: defaultPortType,
      },
    });

    // Watch form values.
    const watchedLocation = watch("locationName");
    const watchedCapacity = watch("portCapacity");
    const watchedContractTerm = watch("contractTerm");
    const watchedPortType = watch("portType");

    // Local state for derived UI options.
    const [availablePortTypes, setAvailablePortTypes] = useState<{
      single: boolean;
      dual: boolean;
    }>({ single: false, dual: false });
    const [availableCapacities, setAvailableCapacities] = useState<string[]>([
      "10gbps",
      "100gbps",
    ]);
    const [noAvailablePorts, setNoAvailablePorts] = useState<boolean>(false);
    const [isDefaultCapacities, setIsDefaultCapacities] = useState(false);

    // Ref to ensure reset() is only called once when editing.
    const didReset = useRef(false);
    useEffect(() => {
      if (isEditing && savedPort && !didReset.current) {
        reset({
          locationName: defaultLocation,
          portCapacity: defaultPortCapacity,
          contractTerm: defaultContractTerm,
          portType: defaultPortType,
        });
        didReset.current = true;
      }
    }, [isEditing, savedPort, reset, defaultLocation, defaultPortCapacity, defaultContractTerm, defaultPortType]);

    // Fetch pricing info on mount.
    useEffect(() => {
      if (!token) return;
      const fetchPricingData = async () => {
        try {
          const response = await dispatch(fetchPricingPlans({ token }));
          if (fetchPricingPlans.fulfilled.match(response)) {
            dispatch(setPricingPlans(response.payload.plans));
          } else {
            throw new Error("Failed to fetch pricing data");
          }
        } catch (error) {
          console.error("Error fetching pricing data:", error);
        }
      };
      fetchPricingData();
    }, [token, dispatch]);

    // When the location or port type changes, update the available options.
    const handleLocationSelect = (value: string) => {
      const selectedDataCenter = locations.find(
        (loc) => loc.datacenterId === value
      );
      if (selectedDataCenter?.port_available) {
        const portAvailable = selectedDataCenter.port_available;
        // Determine if 'single' and 'dual' are available.
        const isSingleAvailable = Object.values(portAvailable.single || {}).some(
          (val) => val === true
        );
        const isDualAvailable = Object.values(portAvailable.dual || {}).some(
          (val) => val === true
        );
        setAvailablePortTypes({ single: isSingleAvailable, dual: isDualAvailable });

        // Derive available capacities from the currently selected port type.
        let capacities = Object.entries(portAvailable[watchedPortType] || {})
          .filter(([_, available]) => available)
          .map(([capacity]) => capacity);
        if (capacities.length === 0) {
          capacities = ["10gbps", "100gbps"];
          setIsDefaultCapacities(true);
        } else {
          setIsDefaultCapacities(false);
        }
        setAvailableCapacities(capacities);
      } else {
        setAvailableCapacities(["10gbps", "100gbps"]);
        setIsDefaultCapacities(true);
        setNoAvailablePorts(true);
      }
    };

    // Update available options whenever the location or port type changes.
    useEffect(() => {
      if (watchedLocation) {
        handleLocationSelect(watchedLocation);
      }
    }, [watchedLocation, watchedPortType, locations]);

    // Update Redux order whenever any of the relevant fields change.
    useEffect(() => {
      const pricingOptions = calculatePricingOptions(
        pricingPlans ?? null,
        watchedPortType,
        watchedCapacity
      );
      const pricingOption = pricingOptions.find(
        (opt) => opt.value === watchedContractTerm
      );
      const termMonths = parseInt(watchedContractTerm, 10);
      const portData: Partial<Port> = {
        basicInfo: {
          name: savedPort?.basicInfo?.name || "",
        },
        location: {
          datacenterId: watchedLocation,
          facilityName:
            dropdownItems.find((item) => item.value === watchedLocation)
              ?.label || "",
        },
        configuration: {
          capacity: {
            speed: watchedCapacity,
            type: watchedPortType,
          },
          term: {
            months: termMonths,
            autoRenew: false,
          },
        },
        pricing: {
          totalMRC: pricingOption ? pricingOption.price : 0,
          currency: pricingOption ? pricingOption.currency : "USD",
        },
      };
      dispatch(updateOrder({ portIndex: 0, portData }));
    }, [
      watchedPortType,
      watchedCapacity,
      pricingPlans,
      watchedContractTerm,
      watchedLocation,
      dispatch,
    ]);

    // Expose a submit method via ref.
    useImperativeHandle(ref, () => ({
      submit: async () =>
        new Promise<boolean>((resolve) => {
          handleSubmit(
            (data) => {
              onSubmit(data);
              resolve(true);
            },
            () => {
              resolve(false);
            }
          )();
        }),
    }));

    const onSubmit: SubmitHandler<PortLocationForm> = (data) => {
      console.log("Form submitted:", data);
    };

    // Map location options.
    const locationOptions = Array.isArray(locations)
      ? locations.map((loc) => ({
          value: loc.datacenterId ?? "-",
          label: loc.displayName ?? "-",
        }))
      : [];

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Port Location */}
        <div className="px-7 pt-5">
          <h3 className="text-2l font-bold mb-8 text-left text-semantic-text-primary">
            Port Location
          </h3>
          <Controller
            name="locationName"
            control={control}
            rules={{ required: "You must select a location" }}
            render={({ field }) => (
              <SearchSelect
                {...field}
                label="Location"
                options={locationOptions}
                leftIcon={<GeolocationIcon className="w-5 h-5 text-gray-400" />}
                onSelect={(value: string) => {
                  field.onChange(value);
                  handleLocationSelect(value);
                }}
              />
            )}
          />
          {errors.locationName && (
            <p className="text-red-500 text-sm">
              {errors.locationName.message}
            </p>
          )}
          <div className="mt-5 w-80% border-b border-gray-300" />
        </div>

        {/* Port Capacity and Port Type */}
        <div className="px-7 pt-5">
          <h3 className="text-2l font-bold mb-4 text-left text-semantic-text-secondary">
            Port Capacity
          </h3>
          <Controller
            name="portCapacity"
            control={control}
            rules={{ required: "You must select a port capacity" }}
            render={({ field }) => (
              <SegmentedControl
                {...field}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                options={availableCapacities.map((capacity) => ({
                  value: capacity,
                  label: capacity.replace(/gbps/i, "G"),
                  disabled: isDefaultCapacities,
                }))}
                size="medium"
                disabled={isDefaultCapacities}
              />
            )}
          />
          {errors.portCapacity && (
            <p className="text-red-500 text-sm">{errors.portCapacity.message}</p>
          )}
          <div className="mt-4 flex flex-col gap-2">
            <Controller
              name="portType"
              control={control}
              rules={{ required: "You must select a port type" }}
              render={({ field }) => (
                <>
                  {[
                    { value: "single", label: "Single" },
                    { value: "dual", label: "Dual" },
                  ].map((option) => (
                    <GenericCheckbox
                      key={option.value}
                      label={option.label}
                      checked={
                        field.value
                          ? field.value === option.value
                          : defaultPortType === option.value
                      }
                      onChange={(checked: boolean) => {
                        if (checked) {
                          field.onChange(option.value);
                        }
                      }}
                      size="md"
                      disabled={!availablePortTypes[option.value as "single" | "dual"]}
                    />
                  ))}
                </>
              )}
            />
          </div>
          {errors.portType && (
            <p className="text-red-500 text-sm">{errors.portType.message}</p>
          )}
          {noAvailablePorts && (
            <p className="text-red-500 text-sm mt-2">
              No available ports at this location.
            </p>
          )}
          <div className="mt-5 w-80% border-b border-gray-300" />
        </div>

        {/* Contract Term */}
        <div className="px-7 pt-5">
          <h3 className="text-2l font-bold mb-4 text-left text-gray-400">
            Contract Term
          </h3>
          <Controller
            name="contractTerm"
            control={control}
            rules={{ required: "You must select a contract term" }}
            render={({ field }) => {
              const pricingOptions = calculatePricingOptions(
                pricingPlans ?? null,
                watchedPortType,
                watchedCapacity
              );
              return (
                <div className="space-y-3">
                  {pricingOptions.map((option) => (
                    <GenericRadioCard
                      key={option.value}
                      name="contractTerm"
                      value={option.value}
                      label={option.label}
                      checked={field.value === option.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="w-[250px]"
                    />
                  ))}
                </div>
              );
            }}
          />
          {errors.contractTerm && (
            <p className="text-red-500 text-sm">{errors.contractTerm.message}</p>
          )}
          <div className="mt-5 w-80% border-b border-gray-300" />
        </div>
      </form>
    );
  }
);

PortLocation.displayName = "PortLocation";
export default PortLocation;
